import React, { RefObject, useRef, useState } from "react";
import { Provider } from "react-redux";
import { Modal } from "@clarkinc/zest-design-system";
import { useComponentStore } from "@wss/store";
import Store from "~/common/Store";
import {
  CompatibleModel,
  MultiSelectTopper,
} from "~/common/Store/models/Category";
import PaginationModel from "~/common/Store/models/Pagination";
import {
  Product,
  PromotedProductBrandAd,
} from "~/common/Store/models/ProductListing";
import { Topper } from "~/common/Store/models/Topper";
import { defaultUserObject } from "~/common/Store/rootReducer";
import useIsMounted from "~/common/useIsMounted";
import FiltersAreSelected from "./FiltersAreSelected";
import ProductBoxes from "./ProductBoxes";
import { PurchasingGroupProps } from "./PurchasingGroupPage/purchasingGroupPageResult";
import Sidebar from "./Sidebar";
import { FacetNavigation } from "./Sidebar/Filters";
import { GroupProps } from "./Sidebar/GroupProps";
import findSelectableFilters from "./Sidebar/findSelectableFilters";
import Chrome from "./Toolbar/Chrome";
import MobileFilters from "./Toolbar/MobileFilters";
import MobileSortBy from "./Toolbar/MobileSortBy";
import FilterModalContextProvider from "./FilterModalContextProvider";
import { CompareProductsState } from "./compareProductStore";
import ChromeStoreContextProvider from "./Toolbar/ChromeStoreContextProvider";
import useChromeBarComponentStore from "./Toolbar/useChromeBarComponentStore";

interface Props {
  hasBackground: boolean;
  hideFilters: boolean;
  pagination: PaginationModel;
  facetNavigation: FacetNavigation;
  products: Product[];
  hasHeaderData: boolean;
  pageName: string;
  pageUrl: string;
  topper?: Topper;
  hideTopper?: boolean;
  showSearchWithinFilter?: boolean;
  showCategoryFilter?: boolean;
  showOutletPoliciesButton?: boolean;
  showOutletConditionFilter?: boolean;
  ignoreCategoryFacets?: boolean;
  aboveChromeComponent?: JSX.Element;
  belowPageComponent?: JSX.Element;
  belowListingComponent?: JSX.Element;
  filterComponentOrder?: ((props: GroupProps) => JSX.Element)[];
  hasCustomizableProductsOverride?: boolean;
  showPartsFilter?: boolean;
  prefetch?: boolean;
  purchasingGroupProps?: PurchasingGroupProps;
  compatibleModels?: CompatibleModel[];
  discountCode?: string;
  showSaleStockNotice?: boolean;
  promotedProductBrandAd?: PromotedProductBrandAd;
  multiSelectTopper?: MultiSelectTopper;
}

const ProductListing = ({
  hasBackground,
  hideFilters,
  pagination,
  facetNavigation,
  products = [],
  hasHeaderData,
  pageName,
  pageUrl,
  topper,
  hideTopper = false,
  showSearchWithinFilter = true,
  ignoreCategoryFacets = false,
  aboveChromeComponent,
  belowPageComponent,
  belowListingComponent,
  filterComponentOrder,
  showCategoryFilter = true,
  hasCustomizableProductsOverride = false,
  showPartsFilter = true,
  showOutletPoliciesButton = false,
  showOutletConditionFilter = false,
  prefetch,
  purchasingGroupProps,
  compatibleModels,
  discountCode,
  showSaleStockNotice = false,
  promotedProductBrandAd,
  multiSelectTopper,
}: Props) => {
  const mounted = useIsMounted();

  const compareProductsStore = useComponentStore<CompareProductsState>(() => ({
    active: false,
    items: [],
  }));

  const chromeBarStore = useChromeBarComponentStore();

  const sortByMobileDialogRef = useRef<HTMLDialogElement>(null);
  const filterMobileDialogRef = useRef<HTMLDialogElement>(null);
  const [isFilterRemoved, setIsFilterRemoved] = useState(false);

  const storedFilterUrl = useRef<{ url: string; confirmed: boolean }>({
    url: "",
    confirmed: false,
  });

  let displayFilters = !hideFilters;
  const filtersSelected = FiltersAreSelected(
    facetNavigation,
    ignoreCategoryFacets,
    pageUrl
  );

  if (displayFilters) {
    const hasSelectableFilters = findSelectableFilters(
      facetNavigation,
      showSearchWithinFilter
    );

    displayFilters =
      filtersSelected ||
      (pagination.totalNumberOfItems > 1 && hasSelectableFilters);
  }

  const toggleShowMobileMenuDialog = (
    dialogRef: RefObject<HTMLDialogElement>
  ) => {
    const dialog = dialogRef.current;
    const modalOpen = dialog?.hasAttribute("open");
    if (modalOpen) {
      dialog?.classList.add("translate-y-[100vh]", "backdrop:opacity-0");
      // This timeout is used to prevent the dialog from being hidden while the animation runs
      setTimeout(() => {
        dialog?.close();
        // Remove the transition classes so that the dialog and backdrop can be shown again later
        dialog?.classList.remove("translate-y-[100vh]", "backdrop:opacity-0");
      }, 150);
    } else {
      dialog?.showModal();
    }
  };

  const topperCount = topper?.sections ? topper.sections.length : 0;
  const displayTopper =
    !hideTopper &&
    !!topper &&
    products.length > 0 &&
    !filtersSelected &&
    topperCount > 0;

  const displayOutletPoliciesButton = showOutletPoliciesButton;

  const facetsWithMultipleSelections: string[] = [];
  const specificationFacetsList = Object.values(
    facetNavigation.appliedFacetValues.specificationFacets
  );
  if (filtersSelected) {
    specificationFacetsList.forEach((facet) => {
      const facetName = facet.split(":");
      if (facetName && facetName.length > 2) {
        facetsWithMultipleSelections.push(facetName[0] || "");
      }
    });
  }

  const isSearchPage =
    pageName === "SearchPage" || pageName === "ImageSearchPage";

  const shouldPreselectVirtualGrouping = isSearchPage || filtersSelected;

  const handleModalConfirm = () => {
    storedFilterUrl.current.confirmed = true;
    Modal.close("confirm-edit-filters-warning");
    window.location.assign(storedFilterUrl.current.url);
  };

  const handleModalCancel = () => {
    Modal.close("confirm-edit-filters-warning");
  };

  return (
    <div
      style={{
        marginTop: hasBackground ? "-50px" : "0px",
      }}
      className={`container container-product-listing${
        displayFilters ? "" : " expand no-filter"
      }`}
    >
      <Modal
        id="confirm-edit-filters-warning"
        data-testid="confirm-edit-filters-warning"
        theme="wss"
        aria-label="Confirm editing filters warning"
        headerTitle="Your Product Selections Will Be Cleared"
        headerProps={{
          className: "flex p-4 items-center",
          closeButtonProps: {
            "aria-label": "Close modal",
          },
          closeButtonIconProps: {
            name: "ri-close-line",
          },
        }}
        footerProps={{
          primaryButtonProps: {
            variant: "green",
            onClick: () => handleModalConfirm(),
            "aria-label": "Confirm. Close modal",
          },
          secondaryButtonText: "Cancel",
          secondaryButtonProps: {
            onClick: () => handleModalCancel(),
            "aria-label": "Cancel. Close Modal",
          },
        }}
        primaryButtonText="Yes, Edit Filters"
      >
        <div className="max-w-lg">
          If you add or edit your filters, your current selections in the
          compare drawer will be cleared.
          <p className="font-semibold pt-4 mb-0 max-w-lg">
            Are you sure you want to edit your filters?
          </p>
        </div>
      </Modal>

      {aboveChromeComponent || (
        <>
          <div className="page-header-container">
            {pageName && (
              <h1 className="text-3xl mt-10 mb-6" data-catid="0">
                {pageName}
              </h1>
            )}
          </div>
          <div className="page-header-content" />
        </>
      )}
      <ChromeStoreContextProvider chromeBarStore={chromeBarStore}>
        <Chrome
          {...{
            hasHeaderData,
            displayFilters,
            pageName,
            pageUrl,
            pagination,
            displayTopper,
            displayOutletPoliciesButton,
            chromeBarStore,
            compareProductsStore,
            toggleShowMobileFilter: () =>
              toggleShowMobileMenuDialog(filterMobileDialogRef),
            toggleShowMobileSortFilter: () =>
              toggleShowMobileMenuDialog(sortByMobileDialogRef),
          }}
        />
        <div
          id="page"
          className={`fixed-go-to-top clears${
            compareProductsStore.useState((state) => state.active)
              ? " compare-mode"
              : ""
          }`}
        >
          <FilterModalContextProvider
            compareProductsStore={compareProductsStore}
            storedFilterUrl={storedFilterUrl}
          >
            {displayFilters && (
              <>
                <MobileSortBy
                  pageUrl={pageUrl}
                  toggleShowMobileSortFilter={() =>
                    toggleShowMobileMenuDialog(sortByMobileDialogRef)
                  }
                  includeRelevancy={isSearchPage}
                  includeMostPopular={!isSearchPage}
                  sortByMobileDialogRef={sortByMobileDialogRef}
                />
                <MobileFilters
                  {...facetNavigation}
                  showCategoryFilter={showCategoryFilter}
                  showOutletConditionFilter={showOutletConditionFilter}
                  pageUrl={pageUrl}
                  showSearchWithinFilter={showSearchWithinFilter}
                  filterComponentOrder={filterComponentOrder}
                  showPartsFilter={showPartsFilter}
                  prefetch={prefetch}
                  toggleShowMobileFilter={() =>
                    toggleShowMobileMenuDialog(filterMobileDialogRef)
                  }
                  compatibleModels={compatibleModels}
                  filterMobileDialogRef={filterMobileDialogRef}
                  isFilterRemoved={isFilterRemoved}
                />
                <Sidebar
                  {...facetNavigation}
                  showCategoryFilter={showCategoryFilter}
                  showOutletConditionFilter={showOutletConditionFilter}
                  pageUrl={pageUrl}
                  showSearchWithinFilter={showSearchWithinFilter}
                  filterComponentOrder={filterComponentOrder}
                  showPartsFilter={showPartsFilter}
                  prefetch={prefetch}
                  compatibleModels={compatibleModels}
                  isFilterRemoved={isFilterRemoved}
                />
              </>
            )}
            <Provider store={Store}>
              <ProductBoxes
                facetNavigation={facetNavigation}
                facetsWithMultipleSelections={facetsWithMultipleSelections}
                ignoreCategoryFacets={ignoreCategoryFacets}
                items={products}
                filtersSelected={filtersSelected}
                pagination={pagination}
                topper={topper}
                pageUrl={pageUrl}
                displayTopper={displayTopper}
                hasCustomizableProductsOverride={
                  hasCustomizableProductsOverride
                }
                chromeBarStore={chromeBarStore}
                compareProductsStore={compareProductsStore}
                purchasingGroupProps={purchasingGroupProps}
                belowListingComponent={belowListingComponent}
                user={defaultUserObject}
                mounted={mounted}
                discountCode={discountCode}
                showSaleStockNotice={showSaleStockNotice}
                shouldPreselectVirtualGrouping={shouldPreselectVirtualGrouping}
                promotedProductBrandAd={promotedProductBrandAd}
                compatibleModels={compatibleModels}
                multiSelectTopper={multiSelectTopper}
                setIsFilterRemoved={setIsFilterRemoved}
              />
              {belowPageComponent}
            </Provider>
          </FilterModalContextProvider>
        </div>
      </ChromeStoreContextProvider>
    </div>
  );
};

export default ProductListing;
